// src/modules/cadete/AvaliacaoCadete.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, query, where, addDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';

// Ícones
const CheckCircleIcon = () => (
  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const ClockIcon = () => (
  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const ArrowRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
);
const ConfettiIcon = () => (
  <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);

const questions = [
    { label: 'Domínio do conteúdo ministrado', name: 'dominio_conteudo', desc: 'O professor demonstra conhecimento profundo sobre a matéria?' },
    { label: 'Didática e clareza na explicação', name: 'didatica', desc: 'O professor consegue transmitir o conteúdo de forma compreensível?' },
    { label: 'Pontualidade e assiduidade', name: 'pontualidade', desc: 'O professor cumpre os horários e comparece às aulas?' },
    { label: 'Relacionamento com a turma', name: 'relacionamento', desc: 'Há respeito e boa comunicação entre professor e alunos?' },
    { label: 'Clareza nos critérios de avaliação', name: 'criterios_avaliacao', desc: 'Os métodos de avaliação foram explicados claramente?' },
    { label: 'Nota Geral', name: 'nota_geral', desc: 'Sua avaliação geral sobre o desempenho deste docente.' }
];

export const AvaliacaoCadete = () => {
    const { userProfile } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [ciclosAtivos, setCiclosAtivos] = useState([]);
    const [professores, setProfessores] = useState([]);
    const [minhasRespostas, setMinhasRespostas] = useState([]);
    
    // Controle de Abas
    const [activeTab, setActiveTab] = useState('pendentes'); // 'pendentes' | 'concluidas'

    // Modal e Form
    const [showModal, setShowModal] = useState(false);
    const [showThankYouModal, setShowThankYouModal] = useState(false);
    const [selectedProf, setSelectedProf] = useState(null);
    const [selectedCicloId, setSelectedCicloId] = useState(null);
    const [formData, setFormData] = useState({});
    const [comentariosGerais, setComentariosGerais] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (!userProfile?.turma_id) { setLoading(false); return; }

        const qCiclos = query(collection(db, paths.avaliacoesCiclos), where("turma_id", "==", userProfile.turma_id), where("ativo", "==", true));
        const unsubCiclos = onSnapshot(qCiclos, (snap) => setCiclosAtivos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const unsubProfs = onSnapshot(collection(db, paths.professores), (snap) => setProfessores(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const qResp = query(collection(db, paths.avaliacoesRespostas), where("cadete_id", "==", userProfile.id));
        const unsubResp = onSnapshot(qResp, (snap) => {
            setMinhasRespostas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        });

        return () => { unsubCiclos(); unsubProfs(); unsubResp(); };
    }, [userProfile]);

    // Cálculo em tempo real do total de pendências
    const totalPendentes = useMemo(() => {
        let count = 0;
        ciclosAtivos.forEach(ciclo => {
            ciclo.professores_ids.forEach(profId => {
                const jaRespondeu = minhasRespostas.some(r => r.ciclo_id === ciclo.id && r.professor_id === profId);
                if (!jaRespondeu) count++;
            });
        });
        return count;
    }, [ciclosAtivos, minhasRespostas]);

    const getMateriaDoProfessor = (prof) => {
        if (!prof.aulas || !userProfile.turma_id) return 'Matéria Geral';
        const aula = prof.aulas.find(a => a.curso_id === userProfile.curso_id) || prof.aulas[0];
        return aula ? aula.materia_nome : 'Matéria não vinculada';
    };

    const handleOpenAvaliacao = (prof, cicloId) => {
        setSelectedProf(prof);
        setSelectedCicloId(cicloId);
        setFormData({});
        setComentariosGerais('');
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (Object.keys(formData).length < questions.length) {
            alert("Por favor, avalie todos os quesitos de 1 a 10.");
            return;
        }

        setProcessing(true);
        try {
            await addDoc(collection(db, paths.avaliacoesRespostas), {
                ciclo_id: selectedCicloId,
                cadete_id: userProfile.id,
                professor_id: selectedProf.id,
                ...formData,
                comentarios_gerais: comentariosGerais,
                respondido_em: serverTimestamp()
            });
            setShowModal(false);
            
            if (totalPendentes === 1) {
                setShowThankYouModal(true);
            }

        } catch (err) {
            alert("Erro ao enviar: " + err.message);
        } finally {
            setProcessing(false);
        }
    };

    const handleFinish = () => {
        setShowThankYouModal(false);
        navigate('/'); 
    };

    const handleChange = (name, val) => {
        setFormData(prev => ({ ...prev, [name]: parseInt(val) }));
    };

    const ScaleInput = ({ name, value, onChange }) => {
        return (
            <div className="flex w-full gap-1 mt-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => {
                    let baseClass = "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100";
                    let activeClass = "";
                    
                    if (i <= 4) activeClass = "bg-red-500 text-white border-red-600";
                    else if (i <= 7) activeClass = "bg-yellow-400 text-white border-yellow-500";
                    else activeClass = "bg-green-500 text-white border-green-600";

                    const isSelected = value === i;

                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => onChange(name, i)}
                            className={`
                                flex-1 h-10 rounded text-xs sm:text-sm font-bold border transition-all duration-150
                                ${isSelected ? activeClass : baseClass}
                                ${isSelected ? 'transform scale-105 shadow-md ring-2 ring-offset-1 ring-blue-100' : ''}
                            `}
                        >
                            {i}
                        </button>
                    );
                })}
            </div>
        );
    };

    if (loading) return <LoadingSpinner />;
    if (!userProfile.turma_id) return <div className="p-8 text-center text-gray-500">Seu usuário não está vinculado a uma Turma.</div>;

    let countConcluidas = 0;
    ciclosAtivos.forEach(c => c.professores_ids.forEach(pid => {
        if(minhasRespostas.some(r => r.ciclo_id === c.id && r.professor_id === pid)) countConcluidas++;
    }));

    return (
        <div className="space-y-6 animate-fade-in">
            {/* CABEÇALHO ATUALIZADO COM O BRASÃO */}
            <header className="flex items-center gap-4 border-b pb-4 mb-6">
                <img 
                    src="https://apmbb.com.br/resultados/brasao.png" 
                    alt="Brasão APMBB" 
                    className="h-16 w-16 object-contain" 
                />
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        Avaliação de Ensino
                    </h1>
                    <p className="text-gray-600 mt-1">Sua opinião é fundamental para a melhoria acadêmica da APMBB.</p>
                </div>
            </header>

            {/* ABAS DE NAVEGAÇÃO */}
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                    onClick={() => setActiveTab('pendentes')}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                        activeTab === 'pendentes'
                            ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <ClockIcon /> Pendentes
                    {totalPendentes > 0 && (
                        <span className="ml-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-bold">
                            {totalPendentes}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('concluidas')}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                        activeTab === 'concluidas'
                            ? 'bg-white text-green-600 shadow-sm ring-1 ring-black/5'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <CheckCircleIcon /> Concluídas
                    {countConcluidas > 0 && (
                        <span className="ml-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                            {countConcluidas}
                        </span>
                    )}
                </button>
            </div>

            {/* CONTEÚDO */}
            {ciclosAtivos.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-gray-500 text-lg">Nenhum ciclo de avaliação aberto no momento.</p>
                </div>
            ) : (
                ciclosAtivos.map(ciclo => {
                    const professoresFiltrados = ciclo.professores_ids
                        .map(id => professores.find(p => p.id === id))
                        .filter(prof => {
                            if (!prof) return false;
                            const jaRespondeu = minhasRespostas.some(r => r.ciclo_id === ciclo.id && r.professor_id === prof.id);
                            return activeTab === 'pendentes' ? !jaRespondeu : jaRespondeu;
                        });

                    if (professoresFiltrados.length === 0) return null;

                    return (
                        <div key={ciclo.id} className="space-y-4">
                            <div className="flex items-center gap-2 mb-2 px-1 pt-2">
                                <span className={`h-6 w-1 rounded-full ${activeTab === 'pendentes' ? 'bg-blue-600' : 'bg-green-600'}`}></span>
                                <h2 className="text-lg font-bold text-gray-800">{ciclo.titulo}</h2>
                            </div>

                            {/* LISTA EM LINHAS E COLUNAS */}
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                                    <div className="col-span-4">Docente</div>
                                    <div className="col-span-2">Posto</div>
                                    <div className="col-span-4">Matéria</div>
                                    <div className="col-span-2 text-right">Ação</div>
                                </div>

                                <div className="divide-y divide-gray-100">
                                    {professoresFiltrados.map(prof => {
                                        const materia = getMateriaDoProfessor(prof);
                                        const jaRespondeu = activeTab === 'concluidas';

                                        return (
                                            <div key={prof.id} className="flex flex-col md:grid md:grid-cols-12 gap-3 p-4 items-center hover:bg-gray-50 transition-colors">
                                                <div className="w-full md:w-auto md:col-span-4 flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${jaRespondeu ? 'bg-green-500' : 'bg-blue-500'}`}>
                                                        {prof.nome_completo.charAt(0)}
                                                    </div>
                                                    <span className="font-bold text-gray-800 text-sm md:text-base">{prof.nome_completo}</span>
                                                </div>
                                                <div className="w-full md:w-auto md:col-span-2 text-sm text-gray-600 pl-11 md:pl-0">
                                                    <span className="md:hidden font-bold text-xs text-gray-400 mr-2">POSTO:</span>
                                                    {prof.posto}
                                                </div>
                                                <div className="w-full md:w-auto md:col-span-4 text-sm text-gray-600 pl-11 md:pl-0">
                                                    <span className="md:hidden font-bold text-xs text-gray-400 mr-2">MATÉRIA:</span>
                                                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs font-medium uppercase">
                                                        {materia}
                                                    </span>
                                                </div>
                                                <div className="w-full md:w-auto md:col-span-2 flex justify-end pl-11 md:pl-0">
                                                    {jaRespondeu ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 cursor-default">
                                                            <CheckCircleIcon />
                                                            <span className="ml-1">Avaliado</span>
                                                        </span>
                                                    ) : (
                                                        <Button 
                                                            onClick={() => handleOpenAvaliacao(prof, ciclo.id)} 
                                                            variant="primary" 
                                                            className="w-full md:w-auto text-xs py-1.5 px-4 flex items-center justify-center gap-1"
                                                        >
                                                            Avaliar <ArrowRightIcon />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

            {activeTab === 'pendentes' && totalPendentes === 0 && ciclosAtivos.length > 0 && (
                <div className="p-8 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-800 font-bold text-lg">Tudo em dia!</p>
                    <p className="text-green-600">Você completou todas as avaliações disponíveis.</p>
                </div>
            )}
            {activeTab === 'concluidas' && countConcluidas === 0 && ciclosAtivos.length > 0 && (
                <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-gray-500">Nenhuma avaliação realizada ainda.</p>
                </div>
            )}

            <Modal show={showModal} onClose={() => setShowModal(false)} title="Avaliação de Desempenho Docente" size="lg">
                <div className="mb-6 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 flex flex-col">
                    <h3 className="font-bold text-blue-900 text-lg">{selectedProf?.nome_completo}</h3>
                    <p className="text-blue-700 text-sm">{selectedProf ? getMateriaDoProfessor(selectedProf) : ''}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {questions.map((q, idx) => (
                        <div key={idx} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                            <div className="mb-3">
                                <label className="block text-base font-bold text-gray-800">
                                    {idx + 1}. {q.label}
                                </label>
                                <p className="text-sm text-gray-500 mt-1">{q.desc}</p>
                            </div>
                            
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <ScaleInput 
                                    name={q.name} 
                                    value={formData[q.name]} 
                                    onChange={handleChange} 
                                />
                                <div className="flex justify-between text-[10px] font-bold text-gray-400 mt-2 px-1 uppercase tracking-wide">
                                    <span>Péssimo (1)</span>
                                    <span>Excelente (10)</span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <label className="block text-base font-bold text-gray-800 mb-2">
                            Apontamentos / Comentários (Opcional)
                        </label>
                        <textarea
                            rows="3"
                            value={comentariosGerais}
                            onChange={(e) => setComentariosGerais(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            placeholder="Escreva aqui..."
                        ></textarea>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={processing}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="success" className="px-8 font-bold shadow-md hover:shadow-lg transform active:scale-95 transition-all" disabled={processing}>
                            {processing ? 'Enviando...' : 'Confirmar Avaliação'}
                        </Button>
                    </div>
                </form>
            </Modal>

            <Modal show={showThankYouModal} onClose={handleFinish} title="Obrigado!" size="md">
                <div className="text-center p-4">
                    <ConfettiIcon />
                    <h3 className="text-2xl font-bold text-gray-800 mt-4 mb-2">Avaliações Concluídas!</h3>
                    <p className="text-gray-600 mb-6">
                        Agradecemos sua colaboração. Suas respostas ajudarão a melhorar a qualidade do ensino na Academia.
                        <br/><br/>
                        A opção de avaliação será removida do seu menu.
                    </p>
                    <Button onClick={handleFinish} variant="primary" className="w-full">
                        Voltar ao Dashboard
                    </Button>
                </div>
            </Modal>
        </div>
    );
};